import { getBorderColorBySubjectName, getColorBySubjectName } from 'Util/myPath';

export const dayNames = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา'];

const currentDayIndex = (new Date().getDay() + 6) % 7;

export const transformApiDataToChartData = (subjectProgresses, studentSemesters, subjectList) => {
  const chartData = dayNames.map((day, index) => {
    const isWeekend = index >= 5;
    const goal =
      studentSemesters && studentSemesters.length > 0
        ? isWeekend
          ? studentSemesters[0].weekendCapWorkload
          : studentSemesters[0].weekdayCapWorkload
        : 0;

    return {
      day,
      subjects: {},
      goal,
      total: 0,
    };
  });

  subjectProgresses.forEach((item) => {
    const date = new Date(item.date);
    const dayIndex = (date.getDay() + 6) % 7;
    const subject = item.subject.name;
    const subjectName =
      subjectList?.find((item) => item.key.toLowerCase() === subject.toLowerCase())?.name ||
      subject;
    const durationInHours = item.duration / 3600;

    if (!chartData[dayIndex].subjects[subjectName]) {
      chartData[dayIndex].subjects[subjectName] = 0;
    }
    chartData[dayIndex].subjects[subjectName] += durationInHours;
    chartData[dayIndex].total += durationInHours;
  });

  return chartData;
};

export const calculateMaxHourValue = (chartData, defaultMaxValue = 10) => {
  const maxTotal = Math.max(...chartData.map((item) => item.total));
  const maxGoal = Math.max(...chartData.map((item) => item.goal));
  return Math.max(Math.ceil(maxTotal) + 1, Math.ceil(maxGoal) + 1, defaultMaxValue);
};

export const getHighlightCurrentDayPlugin = (theme) => {
  return {
    id: 'highlightCurrentDay',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const xAxis = chart.scales.x;

      chart.data.labels.forEach((label, index) => {
        if (label === dayNames[currentDayIndex]) {
          const x = xAxis.getPixelForTick(index);
          const labelWidth = 36;
          const labelHeight = 24;

          ctx.save();
          ctx.fillStyle = theme.colors.myPath.secondary[500];
          ctx.beginPath();
          if (ctx.roundRect) {
            ctx.roundRect(
              x - labelWidth / 2,
              chart.chartArea.bottom + 6,
              labelWidth,
              labelHeight,
              8
            );
          } else {
            ctx.rect(x - labelWidth / 2, chart.chartArea.bottom + 6, labelWidth, labelHeight);
          }
          ctx.fill();
          ctx.restore();
        }
      });
    },
  };
};

export const getAllSubjects = (chartData) => {
  const allSubjects = new Set();
  chartData.forEach((dayData) => {
    Object.keys(dayData.subjects).forEach((subject) => {
      allSubjects.add(subject);
    });
  });
  return Array.from(allSubjects);
};

export const createChartData = (data, theme, haveGoals) => {
  const allSubjects = getAllSubjects(data);

  const maxValue = calculateMaxHourValue(data, 10);

  const datasets = [];

  allSubjects.forEach((subject) => {
    const color = getColorBySubjectName(theme, subject);

    datasets.push({
      label: subject,
      data: data.map((dayData) => dayData.subjects[subject] || 0),
      backgroundColor: color,
      borderColor: color,
      borderWidth: 0.1,
      borderRadius: 0,
      borderSkipped: false,
      maxBarThickness: 16,
    });
  });

  if (haveGoals) {
    datasets.push({
      label: '',
      data: data.map((item) => {
        const totalValue = item.total;
        const goalValue = item.goal;

        if (totalValue < goalValue) {
          return goalValue - totalValue;
        }
        return 0;
      }),
      backgroundColor: theme.colors.myPath.primary[100],
      borderColor: 'transparent',
      borderWidth: 0,
      borderRadius: 0,
      borderSkipped: false,
      maxBarThickness: 16,
    });
  }

  datasets.push({
    label: '',
    data: data.map((item) => {
      const goalValue = item.goal || 0;
      const currentValue = item.total || 0;

      const baseValue = goalValue === 0 || !haveGoals ? currentValue : goalValue;
      const remainingToMax = maxValue - baseValue;
      return remainingToMax > 0 ? remainingToMax : 0;
    }),
    backgroundColor: theme.colors.myPath.gray[50],
    borderColor: theme.colors.myPath.gray[300],
    borderWidth: 0.5,
    borderRadius: {
      topLeft: 20,
      topRight: 20,
      bottomLeft: 0,
      bottomRight: 0,
    },
    borderSkipped: false,
    maxBarThickness: 16,
    pointHoverRadius: 0,
    hoverBackgroundColor: theme.colors.myPath.gray[50],
    hoverBorderColor: theme.colors.myPath.gray[300],
  });

  return {
    labels: dayNames,
    datasets: datasets,
  };
};

export const getOptions = (chartData, haveGoals, theme) => {
  const maxValue = calculateMaxHourValue(chartData, 10);
  const allSubjects = getAllSubjects(chartData);

  const annotations = {};
  chartData.forEach((item, barIndex) => {
    const totalValue = item.total || 0;
    const goalValue = item.goal || 0;
    const subjects = item.subjects;

    for (let i = 0; i <= maxValue - 1; i++) {
      let borderColor;

      if (!haveGoals) {
        borderColor = theme.colors.myPath.gray[300];
      } else if (i <= totalValue) {
        let cumulativeValue = 0;
        let currentSubject = null;

        for (const subjectName of allSubjects) {
          const hours = subjects[subjectName] || 0;
          const previousCumulative = cumulativeValue;
          cumulativeValue += hours;

          if (i > previousCumulative && i <= cumulativeValue) {
            currentSubject = subjectName;
            break;
          }
        }

        borderColor = getBorderColorBySubjectName(theme, currentSubject);
      } else if (i <= goalValue && totalValue < goalValue) {
        borderColor = theme.colors.myPath.primary[300];
      } else {
        borderColor = theme.colors.myPath.gray[300];
      }
      annotations[`grid-line-${barIndex}-${i}`] = {
        type: 'line',
        xMin: barIndex - 0.21,
        xMax: barIndex + 0.21,
        yMin: i,
        yMax: i,
        borderColor: borderColor,
        borderWidth: 0.5,
      };
    }
  });

  const goalAnnotations = chartData.reduce((acc, item, index) => {
    if (item.goal === 0 || !haveGoals) {
      return acc;
    }

    const goalKey = `goal-line-${index}`;

    acc[goalKey] = {
      type: 'line',
      xMin: index - 0.21,
      xMax: index + 0.21,
      yMin: item.goal,
      yMax: item.goal,
      borderColor: theme.colors.myPath.primary[500],
      borderWidth: 1.5,
      borderDash: [1.4, 1.4],
    };

    return acc;
  }, {});

  Object.assign(annotations, goalAnnotations);

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        filter: function (tooltipItem) {
          return tooltipItem.datasetIndex !== tooltipItem.chart.data.datasets.length - 1;
        },
      },
      annotation: {
        annotations,
      },
    },
    scales: {
      x: {
        stacked: true,
        display: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            family: 'Noto Sans Thai',
            size: 14,
          },
          color: function (context) {
            const currentDay = dayNames[currentDayIndex];
            const label = context.chart.data.labels[context.index];

            return label === currentDay
              ? theme.colors.myPath.textColor.primary
              : theme.colors.myPath.textColor.secondary;
          },
          backdropColor: 'transparent',
          showLabelBackdrop: false,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: maxValue,
        display: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 10,
          },
          padding: 4,
          color: theme.colors.myPath.textColor.disabled,
        },
      },
    },
  };
};
